import React, { createContext, FC, useCallback, useContext, useMemo, useState } from 'react';
import Snackbar from 'housecall-ui/dist/DesignSystem/Feedback/Snackbar/Snackbar';

import {
  ShowToastHandler,
  SnackbarConfig,
  ToastControllerContext as ToastControllerContextType,
  ToastId,
} from './types';

const ToastControllerContext = createContext<ToastControllerContextType | null>(null);

const DEFAULT_AUTO_HIDE_DURATION = 5_000;

export const ToastControllerProvider: FC = ({ children }) => {
  const [config, setSnackbarConfig] = useState<SnackbarConfig & { id: string }>();

  const hideToast = useCallback(() => {
    setSnackbarConfig(undefined);
  }, []);

  const showToast: ShowToastHandler = useCallback((message, snackbarConfig) => {
    const snackbar = {
      ...snackbarConfig,
      variant: snackbarConfig?.variant ?? 'error',
      id: Date.now().toString(),
      message,
    } as const;
    setSnackbarConfig(snackbar);

    return snackbar.id as ToastId;
  }, []);

  const contextValue = useMemo(() => ({ showToast, hideToast }), [showToast, hideToast]);

  return (
    <ToastControllerContext.Provider value={contextValue}>
      {children}

      {config ? (
        <Snackbar
          key={config.id}
          anchorOrigin={config.anchorOrigin}
          message={config.message}
          autoHideDuration={config.actionButtonOnClick ? null : DEFAULT_AUTO_HIDE_DURATION}
          onClose={hideToast}
          open
          actionMessage={config.actionButtonMessage}
          onClick={config.actionButtonOnClick}
          variant={config.variant ?? 'success'}
          data-testid={`ToastController__${config.variant ?? 'success'}-snackbar`}
        />
      ) : null}
    </ToastControllerContext.Provider>
  );
};

export const useToastController = () => {
  const context = useContext(ToastControllerContext);

  if (!context) {
    throw new Error('useToastController must be used within ToastControllerProvider provider');
  }

  return context;
};

export function withToastController<T = Record<string, unknown>>(
  WrappedComponent: React.ComponentType<T>
): React.FC<T> {
  return (props) => (
    <ToastControllerProvider>
      <WrappedComponent {...props} />
    </ToastControllerProvider>
  );
}
